// Print-time layer renderer, bound to REAL per-student data (from
// getSectionReportCards). RMS layer types (text, image, rms_*) are drawn by
// the designer's own renderer (../report-card-designer/canvas/rmsRender.tsx)
// with the student's values put where the designer reads its sample values,
// so the printed card is exactly what the Designer shows: same units (pt/mm),
// gradients, auto-fit text and badges. The legacy layer types below keep
// their own renderers.
import type { ReactNode } from "react"
import type { Layer } from "../report-card-designer/canvas/types"
import type { SectionCardStudent, SectionReportCards, RcCourse } from "../../../service/report-card"
import { RmsLayerBody, isRmsType, type RmsTpl } from "../report-card-designer/canvas/rmsRender"

type Ctx = {
  school: SectionReportCards["school"]
  breakdown: SectionReportCards["breakdown"]
  klass: SectionReportCards["class"]
  section: SectionReportCards["section"]
  tpl: { columns?: Record<string, boolean>; signatures?: string[]; accentColor?: string }
  rmsTpl: RmsTpl
  plan: SubjectPlan
  assetUrls: Record<string, string>
  onNeedUrl?: (key: string) => void
  gradeLegend: SectionReportCards["gradeLegend"]
}

// ── Token interpolation ─────────────────────────────────────────────────
// Builds the SAME nested scope DesignerCard.jsx's real templates address
// ({{student.full_name}}, {{campus.address}}, {{grade_scale.key_with_label}},
// etc.) so free-text "text" layers and "dynamic-text" layers both resolve
// against real data instead of printing the raw token.
const buildScope = (student: SectionCardStudent, ctx: Ctx) => {
  const pairs = ctx.gradeLegend
    .map(g => (g.remarks ? `${g.grade} : ${g.remarks}` : g.grade))
  // RMS's format: "A* (90-100%)", "(100%)" when both bounds match, letter alone without bounds.
  const pctPairs = ctx.gradeLegend.map(g => {
    if (!Number.isFinite(g.minPercentage) || !Number.isFinite(g.maxPercentage)) return g.grade
    return `${g.grade} (${g.minPercentage === g.maxPercentage ? `${g.minPercentage}%` : `${g.minPercentage}-${g.maxPercentage}%`})`
  })
  const gradeScale = {
    key: pairs.join(" | "),
    key_with_label: pairs.length ? `Grading Key | ${pairs.join(" | ")}` : "",
    key_multiline: pairs.join("\n"),
    key_pct_only: pctPairs.join(" | "),
    name: "",
  }
  return {
    student: {
      full_name: student.studentName,
      gr_number: student.grNo,
      gr_no: student.grNo,
      roll_number: student.rollNo,
      roll_no: student.rollNo,
      grade_letter: student.summary.overallGrade || "",
      result: student.summary.overallPassFail || "",
      promotion_status: student.summary.promotionStatus || "",
    },
    class: { name: ctx.klass.name },
    section: { name: ctx.section.name },
    school: { name: ctx.school.name, code: ctx.school.code, address: ctx.school.address, phone: ctx.school.phone },
    // RMS templates address the campus under "campus.*" — alias onto the same data.
    campus: { name: ctx.school.name, address: ctx.school.address, phone: ctx.school.phone },
    breakdown: { title: ctx.breakdown.title, term: ctx.breakdown.term || "", session_name: ctx.breakdown.session || "" },
    attendance: { days_present: student.attendance.daysPresent, days_total: student.attendance.daysTotal },
    summary: {
      overall_grade: student.summary.overallGrade || "",
      overall_percentage: student.summary.overallPercentage,
      position: student.summary.positionWithSuffix,
      result: student.summary.overallPassFail || "",
      promotion_status: student.summary.promotionStatus || "",
    },
    grade_scale: gradeScale,
  }
}

const TOKEN_FILTERS: Record<string, (s: string) => string> = {
  upper: s => s.toUpperCase(),
  lower: s => s.toLowerCase(),
  title: s => s.replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase()),
  sentence: s => (s.length ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s),
}

// Resolve every `{{path.to.value | filter}}` in `text` against the nested
// `scope` object. Unknown paths resolve to "" (silent) so a misconfigured
// template doesn't crash the page — same behaviour as the server renderer.
const interpolate = (text: string, scope: Record<string, any>): string => {
  if (typeof text !== "string") return ""
  return text.replace(/\{\{([^}]+)\}\}/g, (_, raw) => {
    const segments = String(raw).split("|").map((s: string) => s.trim()).filter(Boolean)
    if (!segments.length) return ""
    const [pathRaw, ...filterNames] = segments
    let cur: any = scope
    for (const seg of pathRaw.split(".")) {
      if (cur == null) return ""
      cur = cur[seg]
    }
    if (cur == null || typeof cur === "object") return ""
    let out = String(cur)
    for (const f of filterNames) out = (TOKEN_FILTERS[f.toLowerCase()] || (v => v))(out)
    return out
  })
}

// Resolve a `layer.field` path (used by "dynamic-text") against the same
// nested scope `interpolate` uses, so both token styles share one source.
const resolveField = (field: string | undefined, scope: Record<string, any>): string => {
  if (!field) return ""
  let cur: any = scope
  for (const seg of field.split(".")) {
    if (cur == null) return ""
    cur = cur[seg]
  }
  return cur == null || typeof cur === "object" ? "" : String(cur)
}

// ── Grade colours ────────────────────────────────────────────────────────
// Same precedence as RMS's print (DesignerCard.jsx paletteForGrade): F/U/AB
// keep the template's own fail colours; any other letter takes the
// template's per-letter override, else the live Grade Scale colour, else the
// designer's built-in palette. "NA" (not assessed) is a neutral grey medal,
// not a fail colour.
const FAIL_GRADES = new Set(["F", "U", "AB"])
const NA_PALETTE = { outer: "#9ca3af", inner: "#e5e7eb", letterColor: "#374151", stroke: "#4b5563" }

const paletteFromHex = (hex: string) => {
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null
  const n = parseInt(hex.slice(1), 16)
  const rgb = [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
  const toward = (target: number, t: number) =>
    `#${rgb.map(c => Math.round(c + (target - c) * t).toString(16).padStart(2, "0")).join("")}`
  const lin = (c: number) => (c / 255 <= 0.03928 ? c / 255 / 12.92 : Math.pow((c / 255 + 0.055) / 1.055, 2.4))
  const lum = 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2])
  return { outer: hex, inner: toward(255, 0.55), letterColor: lum < 0.45 ? "#ffffff" : "#0f172a", stroke: toward(0, 0.45) }
}

const withLiveColors = (l: Record<string, any>, letter: string, liveHex: string | null) => {
  const L = letter.toUpperCase()
  if (FAIL_GRADES.has(L) || l.gradePalette?.[L]) return l
  const p = L === "NA" ? NA_PALETTE : liveHex ? paletteFromHex(liveHex) : null
  return p ? { ...l, gradePalette: { ...(l.gradePalette || {}), [L]: p } } : l
}

// ── Which subject each card shows ────────────────────────────────────────
type AnyCourse = RcCourse | SectionCardStudent["missingCourses"][number]
export type SubjectPlan = {
  // card layer id → the subject it shows (matched: by its own designed name),
  // or null when the class has no subject left for it (card hidden)
  byTable: Map<string, { course: AnyCourse; matched: boolean } | null>
  // folder (groupId) → its card's layer id, so the folder's badge and icon
  // follow that card
  tableByGroup: Map<string, string>
  // images only one card's folder carries: its subject icon (the badge frame
  // every card shares is not one)
  iconSrcs: Set<string>
}

const SUBJECT_TABLES = new Set(["rms_subject_table", "rms_marks_table"])
const norm = (s?: string) => String(s || "").toLowerCase().trim()

const cleanSubjectName = (name?: string) =>
  String(name || "").replace(/^subject\s*\[\d+\]\s*/i, "").replace(/\s*\(\d+sc\)\s*$/i, "").trim()

// RMS's slot match: the card's sample subject by exact name, then a ≥4-char
// prefix on the shorter side (MATH ↔ MATHEMATICS).
const matchByName = (all: AnyCourse[], name: string) => {
  const t = norm(name)
  if (!t) return undefined
  return all.find(c => norm(c.courseName) === t) ?? all.find(c => {
    const n = norm(c.courseName)
    const [shorter, longer] = n.length <= t.length ? [n, t] : [t, n]
    return shorter.length >= 4 && longer.startsWith(shorter)
  })
}

// RMS falls back to the card's slot index when its sample subject isn't in
// this class, which repeats a subject another card already shows (one design
// serves classes with different subjects). Here an unmatched card takes a
// subject no card named instead, and a card with none left is hidden.
export const planSubjects = (layers: any[], student: SectionCardStudent): SubjectPlan => {
  const all: AnyCourse[] = [...student.courses, ...student.missingCourses]
  const tables = layers.filter(l => SUBJECT_TABLES.has(l.type) && !l.hidden)
  const byTable: SubjectPlan["byTable"] = new Map()
  const used = new Set<string>()
  for (const t of tables) {
    const c = matchByName(all, t.sampleSubject?.name || cleanSubjectName(t.name))
    if (c && !used.has(c.courseId)) {
      used.add(c.courseId)
      byTable.set(t.id, { course: c, matched: true })
    }
  }
  // Name order, not this student's graded-first order, so every student in
  // the section gets the same subject in the same slot.
  const rest = all.filter(c => !used.has(c.courseId)).sort((a, b) => a.courseName.localeCompare(b.courseName))
  for (const t of tables) {
    if (byTable.has(t.id)) continue
    const c = rest.shift()
    byTable.set(t.id, c ? { course: c, matched: false } : null)
  }
  const tableByGroup = new Map<string, string>()
  for (const t of tables) if (t.groupId) tableByGroup.set(t.groupId, t.id)
  const foldersBySrc = new Map<string, Set<string>>()
  for (const l of layers) {
    if (l.type !== "image" || !l.src || !l.groupId || !tableByGroup.has(l.groupId)) continue
    foldersBySrc.set(l.src, (foldersBySrc.get(l.src) ?? new Set<string>()).add(l.groupId))
  }
  const iconSrcs = new Set(Array.from(foldersBySrc).filter(([, groups]) => groups.size === 1).map(([src]) => src))
  return { byTable, tableByGroup, iconSrcs }
}

// A badge outside any card's folder (e.g. an older template's loose badge):
// its own name, then its slot index, as before.
const findCourse = (student: SectionCardStudent, name?: string, subjectIndex?: number): AnyCourse | null => {
  const all: AnyCourse[] = [...student.courses, ...student.missingCourses]
  const hit = matchByName(all, cleanSubjectName(name))
  if (hit) return hit
  if (Number.isFinite(subjectIndex) && all[subjectIndex as number]) return all[subjectIndex as number]
  return null
}

const fmt = (v: number | null | undefined, dp = 2) => {
  if (v == null) return "—"
  const n = Number(v)
  if (Number.isNaN(n)) return "—"
  return n % 1 === 0 ? String(n) : n.toFixed(dp).replace(/\.?0+$/, "")
}

const isMissingCourse = (c: AnyCourse): c is SectionCardStudent["missingCourses"][number] => !("obtainedMarks" in c)
const letterFor = (c: AnyCourse) =>
  isMissingCourse(c) ? "NA" : (c as RcCourse).status === "Absent" ? "AB" : (c as RcCourse).grade || "—"
const legendHex = (ctx: Ctx, letter: string) =>
  ctx.gradeLegend.find(g => String(g.grade).toUpperCase() === letter.toUpperCase())?.colorCode ?? null
const liveHexFor = (ctx: Ctx, c: AnyCourse | null, letter: string) =>
  (c && !isMissingCourse(c) ? (c as RcCourse).colorCode : null) ?? legendHex(ctx, letter)

// The designer's `sampleSubject` shape, filled from the real subject
// (DesignerCard.jsx SubjectTableLayer builds the same).
const sampleFor = (c: AnyCourse) => {
  const real = isMissingCourse(c) ? [] : (c as RcCourse).cells
  return {
    name: cleanSubjectName(c.courseName) || c.courseName,
    total: isMissingCourse(c) ? "NA" : fmt((c as RcCourse).obtainedMarks),
    max: c.subCategories.reduce((a, x) => a + x.marks, 0),
    grade: letterFor(c),
    cells: c.subCategories.map((sc, i) => {
      const r = real[i]
      return [sc.description, sc.marks, !r || r.is_na ? "NA" : r.is_absent ? "AB" : fmt(r.value)]
    }),
  }
}

// The layer as the designer renderer should see it: its own designer props
// (top level; _rmsProps is only the frozen RMS-import snapshot) with the
// student's real values where it reads sample ones. null hides the layer.
const toRealLayer = (layer: Layer, student: SectionCardStudent, ctx: Ctx): Record<string, any> | null => {
  const l = { ...((layer as any)._rmsProps || {}), ...layer } as Record<string, any>
  const card = l.groupId ? ctx.plan.tableByGroup.get(l.groupId) : undefined
  const slot = card === undefined ? undefined : ctx.plan.byTable.get(card) ?? null
  switch (l.type) {
    case "text": {
      const t = interpolate(l.text ?? l.content ?? "", buildScope(student, ctx))
      return { ...l, text: t, content: t, sample: t }
    }
    case "image":
      if (slot === undefined) return l
      if (!slot) return null
      return !slot.matched && ctx.plan.iconSrcs.has(l.src) ? null : l
    case "rms_photo": {
      const url = student.photo ? ctx.assetUrls[student.photo] : undefined
      if (student.photo && !url) ctx.onNeedUrl?.(student.photo)
      return { ...l, samplePhotoUrl: url }
    }
    case "rms_attendance_bar":
      return { ...l, samplePresent: student.attendance.daysPresent, sampleTotal: student.attendance.daysTotal }
    case "rms_congrats":
      return {
        ...l,
        sampleGrade: student.summary.overallGrade || "—",
        sampleName: student.studentName.split(/\s+/)[0] || "",
        sampleBlurb: student.blurb || "Keep working hard!",
        sampleIsFail: student.summary.overallPassFail === "Fail",
      }
    case "rms_grade_badge": {
      if (slot !== undefined) {
        if (!slot) return null
        const letter = letterFor(slot.course)
        return withLiveColors({ ...l, sampleGrade: letter }, letter, liveHexFor(ctx, slot.course, letter))
      }
      const course = findCourse(student, l.sampleSubject?.name || l.name, l.subjectIndex)
      const letter = course ? letterFor(course) : student.summary.overallGrade || "—"
      return withLiveColors({ ...l, sampleGrade: letter }, letter, liveHexFor(ctx, course, letter))
    }
    case "rms_subject_table":
    case "rms_marks_table": {
      const a = ctx.plan.byTable.get(l.id)
      if (!a) return null
      const sample = sampleFor(a.course)
      const liveHex = liveHexFor(ctx, a.course, sample.grade)
      return { ...withLiveColors({ ...l, sampleSubject: sample, fillHeight: true }, sample.grade, liveHex), sampleGradeHex: liveHex ?? undefined }
    }
    default:
      return l
  }
}

export const renderPrintLayer = (layer: Layer, scale: number, student: SectionCardStudent, ctx: Ctx): ReactNode => {
  if (isRmsType(layer.type)) {
    const real = toRealLayer(layer, student, ctx)
    return real ? <RmsLayerBody layer={real} assetUrls={ctx.assetUrls} tpl={ctx.rmsTpl} onNeedUrl={ctx.onNeedUrl} /> : null
  }

  const s = (v: number) => v * scale
  const col = ctx.tpl.columns || {}
  const allOff = !col.showObtained && !col.showTotal && !col.showPercent && !col.showGrade && !col.showRemarks
  const S = {
    obt: allOff || col.showObtained !== false,
    tot: allOff || col.showTotal !== false,
    pct: allOff || col.showPercent !== false,
    grd: allOff || col.showGrade !== false,
    res: allOff || col.showRemarks !== false,
  }

  switch (layer.type) {
    case "dynamic-text": {
      const scope = buildScope(student, ctx)
      const value = resolveField(layer.field, scope)
      return (
        <div style={{ width: "100%", height: "100%", fontFamily: layer.fontFamily || "inherit", fontSize: s(layer.fontSize || 14), fontWeight: layer.fontWeight || "normal", color: layer.color || "#0f172a", textAlign: (layer.textAlign as any) || "left", lineHeight: layer.lineHeight || 1.2, overflow: "hidden", padding: s(4), opacity: layer.opacity ?? 1 }}>
          {layer.prefix || ""}{value}{layer.suffix || ""}
        </div>
      )
    }

    case "shape":
      return (
        <div style={{ width: "100%", height: "100%", background: layer.fill || "transparent", border: `${s(layer.strokeWidth || 1)}px solid ${layer.stroke || "transparent"}`, borderRadius: layer.shape === "ellipse" ? "50%" : s(layer.borderRadius || 0), opacity: layer.opacity ?? 1 }} />
      )

    case "line":
      return (
        <div style={{ width: "100%", height: s(layer.thickness || 2), background: layer.color || "#0f172a", borderRadius: layer.rounded ? s(layer.thickness || 2) / 2 : 0, position: "absolute", top: "50%", transform: "translateY(-50%)" }} />
      )

    case "header":
      return (
        <div style={{ width: "100%", height: "100%", background: layer.headerColor || "#0f172a", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: `${s(8)}px ${s(16)}px`, boxSizing: "border-box" }}>
          <div style={{ fontWeight: 800, fontSize: s(18), letterSpacing: s(1.5), textTransform: "uppercase" }}>{ctx.school.name || "School"}</div>
          <div style={{ fontSize: s(10), marginTop: s(4), opacity: 0.85 }}>{ctx.breakdown.title}</div>
        </div>
      )

    case "student_info":
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", gap: s(8), padding: s(6), boxSizing: "border-box", background: "#f9fafb" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: s(13), color: "#111827", marginBottom: s(2) }}>{student.studentName}</div>
            <div style={{ fontSize: s(9), color: "#374151", lineHeight: 1.6 }}>
              <div>GR No: {student.grNo} &nbsp;|&nbsp; Roll No: {student.rollNo || "—"}</div>
              <div>Class: {ctx.klass.name} &nbsp;|&nbsp; Section: {ctx.section.name}</div>
            </div>
          </div>
          {layer.showPhoto && (
            <div style={{ width: s(56), height: s(70), background: "#e5e7eb", border: `${s(1)}px solid #d1d5db`, borderRadius: s(4), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
              {student.photo && ctx.assetUrls[student.photo]
                ? <img src={ctx.assetUrls[student.photo]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <i className="ti ti-user" style={{ fontSize: s(24), color: "#9ca3af" }} />}
            </div>
          )}
        </div>
      )

    case "subject_table": {
      const allCourses = [...student.courses, ...student.missingCourses]
      const thBg = layer.headerColor || "#0f172a"
      const fs = s(8.5)
      const pad = `${s(3)}px ${s(5)}px`
      return (
        <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs, tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: thBg, color: "#fff" }}>
                <th style={{ padding: pad, textAlign: "left", fontWeight: 700, width: "36%" }}>Subject</th>
                {S.obt && <th style={{ padding: pad, width: "20%" }}>Marks</th>}
                {S.grd && <th style={{ padding: pad, width: "13%" }}>Grade</th>}
              </tr>
            </thead>
            <tbody>
              {allCourses.map((c, i) => {
                const isMissing = !("obtainedMarks" in c)
                return (
                  <tr key={c.courseId} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb" }}>
                    <td style={{ padding: pad, borderBottom: `${s(0.5)}px solid #e5e7eb`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.courseName}</td>
                    {S.obt && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb` }}>{isMissing ? "NA" : `${fmt((c as RcCourse).obtainedMarks)}/${fmt(c.subCategories.reduce((a, x) => a + x.marks, 0))}`}</td>}
                    {S.grd && <td style={{ padding: pad, textAlign: "center", borderBottom: `${s(0.5)}px solid #e5e7eb`, color: ctx.tpl.accentColor || "#1a56db", fontWeight: 700 }}>{isMissing ? "NA" : (c as RcCourse).grade || "—"}</td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )
    }

    case "grade_key": {
      const rows = ctx.gradeLegend.length
        ? ctx.gradeLegend.map(g => [g.grade, `≥${g.minPercentage}%`])
        : [["A+", "90-100"], ["A", "80-89"], ["B", "70-79"], ["C", "60-69"], ["D", "50-59"], ["F", "Below 50"]]
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", border: `${s(1)}px solid #e5e7eb`, borderRadius: s(2) }}>
          <div style={{ fontSize: s(8), fontWeight: 700, color: "#374151", marginBottom: s(4), textTransform: "uppercase" }}>Grade Key</div>
          <div style={{ display: "flex", gap: s(8), flexWrap: "wrap" }}>
            {rows.map(([g, r]) => (
              <div key={g} style={{ fontSize: s(8) }}>
                <span style={{ fontWeight: 700, color: ctx.tpl.accentColor || "#1a56db" }}>{g}</span>
                <span style={{ color: "#6b7280", marginLeft: s(2) }}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }

    case "signatures": {
      const sigs = ctx.tpl.signatures?.length ? ctx.tpl.signatures : ["Class Teacher", "Principal", "Date"]
      return (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "flex-end", borderTop: `${s(1)}px solid #e5e7eb`, paddingTop: s(4), boxSizing: "border-box" }}>
          {sigs.map((sig, i) => (
            <div key={i} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ borderTop: `${s(1)}px solid #0f172a`, marginInline: s(12), paddingTop: s(3), fontSize: s(9), color: "#374151" }}>{sig}</div>
            </div>
          ))}
        </div>
      )
    }

    case "attendance": {
      const present = student.attendance.daysPresent, total = student.attendance.daysTotal
      const ratio = total > 0 ? Math.min(1, present / total) : 0
      return (
        <div style={{ width: "100%", height: "100%", padding: s(6), boxSizing: "border-box", background: "#f0fdf4", border: `${s(1)}px solid #bbf7d0`, borderRadius: s(3) }}>
          <div style={{ fontSize: s(9), fontWeight: 600, color: "#15803d", marginBottom: s(3) }}>Attendance: {present} / {total} days</div>
          <div style={{ height: s(7), background: "#dcfce7", borderRadius: s(4), overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${ratio * 100}%`, background: "#16a34a", borderRadius: s(4) }} />
          </div>
        </div>
      )
    }

    default:
      return null
  }
}

export default renderPrintLayer
